import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  Play,
  ChevronLeft,
  ChevronRight,
  Dumbbell,
  Timer,
  Repeat,
  Flame,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { base44 } from "@/api/base44Client";
import { normalizeExercises } from "@/lib/ActiveWorkoutContext";

function parseInstructions(exercise) {
  if (!exercise) return [];
  let list = [];
  if (Array.isArray(exercise.instructions) && exercise.instructions.length > 0) {
    list = exercise.instructions;
  } else if (typeof exercise.instructions === "string" && exercise.instructions.trim()) {
    try {
      const parsed = JSON.parse(exercise.instructions);
      if (Array.isArray(parsed)) list = parsed;
      else list = [exercise.instructions];
    } catch {
      list = exercise.instructions.split(/\n+/).map((s) => s.trim()).filter(Boolean);
    }
  } else if (exercise.description && typeof exercise.description === "string") {
    list = exercise.description.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  }

  return list
    .map((step) => {
      if (typeof step !== "string") return String(step || "");
      return step.replace(/^(?:Step\s*[:#]?\s*\d+\s*[:\-.]?\s*|\d+[\.\):]\s*)/i, "").trim();
    })
    .filter(Boolean);
}

export default function ExercisePreviewModal({
  open,
  onClose,
  exercises = [],
  initialIndex = 0,
  allExercises = [],
  workoutName = "Workout",
  onStartWorkout,
}) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  // Query exercise library if allExercises wasn't provided
  const { data: catalogue = [] } = useQuery({
    queryKey: ["exercises-catalogue"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
    staleTime: 1000 * 60 * 60,
    enabled: open && (!allExercises || allExercises.length === 0),
  });

  const mergedCatalogue = useMemo(() => {
    if (allExercises && allExercises.length > 0) return allExercises;
    return catalogue;
  }, [allExercises, catalogue]);

  const parsedExercises = useMemo(() => {
    return normalizeExercises(exercises);
  }, [exercises]);

  const totalExercises = parsedExercises.length;
  const currentExercise = parsedExercises[currentIndex] || parsedExercises[0] || null;

  // Resolve matching full exercise from catalogue using 3-tier matching
  const fullExercise = useMemo(() => {
    if (!currentExercise) return null;
    const name = (currentExercise.name || "").toLowerCase().trim();
    if (!name) return null;

    // 1. Exact match
    let found = mergedCatalogue.find(
      (e) => (e.name || "").toLowerCase().trim() === name
    );
    if (found) return found;

    // 2. Normalized match (dashes, parentheses, extra whitespace)
    const clean = (s) =>
      s
        .toLowerCase()
        .replace(/[-_]/g, " ")
        .replace(/[()]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    const cleanCur = clean(name);
    found = mergedCatalogue.find((e) => clean(e.name || "") === cleanCur);
    if (found) return found;

    // 3. Substring match
    found = mergedCatalogue.find((e) => {
      const cleanE = clean(e.name || "");
      return cleanE && (cleanE.includes(cleanCur) || cleanCur.includes(cleanE));
    });
    return found || null;
  }, [currentExercise, mergedCatalogue]);

  const instructions = useMemo(() => {
    const list = parseInstructions(currentExercise);
    if (list.length > 0) return list;
    return parseInstructions(fullExercise);
  }, [currentExercise, fullExercise]);

  const instructionsTypography = useMemo(() => {
    const count = instructions.length;
    if (count <= 3) {
      return {
        fontSize: "clamp(0.9rem, 1.8vh, 1.05rem)",
        lineHeight: "1.6",
        gap: "clamp(0.6rem, 1.8vh, 1.1rem)",
      };
    }
    if (count <= 5) {
      return {
        fontSize: "clamp(0.82rem, 1.4vh, 0.95rem)",
        lineHeight: "1.52",
        gap: "clamp(0.45rem, 1.4vh, 0.9rem)",
      };
    }
    return {
      fontSize: "clamp(0.78rem, 1.2vh, 0.88rem)",
      lineHeight: "1.48",
      gap: "clamp(0.35rem, 1.1vh, 0.75rem)",
    };
  }, [instructions.length]);

  if (!open || totalExercises === 0) return null;

  const gifUrl =
    currentExercise?.gifUrl ||
    currentExercise?.gif_url ||
    currentExercise?.media ||
    currentExercise?.video_url ||
    fullExercise?.gifUrl ||
    fullExercise?.gif_url ||
    fullExercise?.media ||
    fullExercise?.video_url ||
    null;

  const targetMuscles = (() => {
    const fromCur = Array.isArray(currentExercise?.targetMuscles)
      ? currentExercise.targetMuscles
      : typeof currentExercise?.muscles_primary === "string"
      ? currentExercise.muscles_primary.split(/,\s*/).filter(Boolean)
      : [];
    if (fromCur.length > 0) return fromCur;
    if (Array.isArray(fullExercise?.targetMuscles)) return fullExercise.targetMuscles;
    if (typeof fullExercise?.muscles_primary === "string") {
      return fullExercise.muscles_primary.split(/,\s*/).filter(Boolean);
    }
    return [];
  })();

  const equipments = (() => {
    const fromCur = Array.isArray(currentExercise?.equipments)
      ? currentExercise.equipments
      : currentExercise?.equipment
      ? [currentExercise.equipment]
      : [];
    if (fromCur.length > 0) return fromCur;
    if (Array.isArray(fullExercise?.equipments)) return fullExercise.equipments;
    if (fullExercise?.equipment) return [fullExercise.equipment];
    return [];
  })();

  const sets = currentExercise?.sets || 3;
  const reps = currentExercise?.reps || "8-12";
  const restSec = currentExercise?.rest_seconds || 60;

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(totalExercises - 1, prev + 1));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-5 select-none animate-in fade-in duration-200">
      <div
        className="bg-[#0B1220] border border-white/15 rounded-3xl w-full max-w-lg max-h-[92dvh] flex flex-col overflow-hidden shadow-2xl relative"
        style={{
          paddingTop: "max(12px, env(safe-area-inset-top))",
          paddingBottom: "max(12px, env(safe-area-inset-bottom))",
        }}
      >
        {/* Top Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold tracking-widest uppercase text-sky-400">
              Exercise Preview
            </span>
            <span className="text-[10px] text-slate-400 font-semibold">
              ({currentIndex + 1} of {totalExercises})
            </span>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/80 hover:text-white transition active:scale-95"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 scrollbar-thin">
          {/* GIF Demonstration */}
          <div className="w-full h-52 sm:h-64 rounded-2xl overflow-hidden bg-black/50 border border-white/10 flex items-center justify-center relative shadow-inner shrink-0">
            {gifUrl ? (
              <img
                src={gifUrl}
                alt={currentExercise.name}
                className="w-full h-full object-contain"
                loading="eager"
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-slate-500 gap-2">
                <Dumbbell className="w-12 h-12 opacity-40" />
                <span className="text-xs font-medium">Demo unavailable</span>
              </div>
            )}

            {/* Muscle Badge */}
            {targetMuscles.length > 0 && (
              <div className="absolute top-2.5 right-2.5 bg-[#050A12]/80 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/15 text-[10px] font-semibold text-white/90 capitalize">
                {targetMuscles.join(", ")}
              </div>
            )}
          </div>

          {/* Exercise Title & Tags */}
          <div className="space-y-1.5">
            <h2 className="text-xl sm:text-2xl font-display font-extrabold capitalize text-white tracking-tight">
              {currentExercise.name}
            </h2>

            <div className="flex flex-wrap gap-1.5">
              {targetMuscles.map((m) => (
                <Badge
                  key={m}
                  variant="secondary"
                  className="text-[10px] capitalize bg-sky-500/10 text-sky-300 border-none"
                >
                  {m}
                </Badge>
              ))}
              {equipments.map((eq) => (
                <Badge
                  key={eq}
                  variant="outline"
                  className="text-[10px] capitalize border-amber-500/30 text-amber-400 bg-amber-500/10"
                >
                  {eq}
                </Badge>
              ))}
            </div>
          </div>

          {/* Prescribed Stats: Sets, Reps, Rest */}
          <div className="grid grid-cols-3 gap-2 bg-[#101C2E] border border-white/10 rounded-2xl p-3">
            <div className="text-center">
              <p className="text-[10px] font-bold uppercase text-slate-400 mb-0.5">Sets</p>
              <p className="text-sm font-extrabold text-white">{sets}</p>
            </div>
            <div className="text-center border-x border-white/10">
              <p className="text-[10px] font-bold uppercase text-slate-400 mb-0.5">Reps</p>
              <p className="text-sm font-extrabold text-white">{reps}</p>
            </div>
            <div className="text-center">
              <p className="text-[10px] font-bold uppercase text-slate-400 mb-0.5">Rest</p>
              <p className="text-sm font-extrabold text-white">{restSec}s</p>
            </div>
          </div>

          {/* ALWAYS-VISIBLE Instructions */}
          <div className="space-y-2 bg-[#101C2E]/60 border border-white/10 rounded-2xl p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
              How to Perform
            </p>
            {instructions.length > 0 ? (
              <ol className="flex flex-col pr-1" style={{ gap: instructionsTypography.gap }}>
                {instructions.map((step, idx) => (
                  <li key={idx} className="flex items-start gap-2.5">
                    <span
                      className="text-sky-400 font-bold shrink-0 mt-0.5"
                      style={{ fontSize: instructionsTypography.fontSize, minWidth: "16px" }}
                    >
                      {idx + 1}.
                    </span>
                    <span
                      className="text-slate-300"
                      style={{
                        fontSize: instructionsTypography.fontSize,
                        lineHeight: instructionsTypography.lineHeight,
                      }}
                    >
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <p
                className="text-slate-400 italic"
                style={{
                  fontSize: instructionsTypography.fontSize,
                  lineHeight: instructionsTypography.lineHeight,
                }}
              >
                Maintain steady form, engage core, and control the eccentric motion throughout each rep.
              </p>
            )}
          </div>
        </div>

        {/* Bottom Actions: Navigation + Start Workout */}
        <div className="px-4 py-3 border-t border-white/10 shrink-0 space-y-2.5 bg-[#080E18]">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              disabled={currentIndex <= 0}
              onClick={handlePrev}
              className="flex-1 py-2.5 px-3 rounded-xl bg-white/10 hover:bg-white/15 disabled:opacity-30 disabled:pointer-events-none text-xs font-bold text-white flex items-center justify-center gap-1 active:scale-95 transition"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>

            <button
              type="button"
              disabled={currentIndex >= totalExercises - 1}
              onClick={handleNext}
              className="flex-1 py-2.5 px-3 rounded-xl bg-white/10 hover:bg-white/15 disabled:opacity-30 disabled:pointer-events-none text-xs font-bold text-white flex items-center justify-center gap-1 active:scale-95 transition"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {onStartWorkout && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onStartWorkout();
              }}
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 text-white font-extrabold text-sm tracking-wide shadow-[0_0_20px_rgba(14,165,233,0.4)] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <Play className="w-4 h-4 fill-white" /> START WORKOUT
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
